/* global */
/* eslint no-undef: "error"*/
import values from 'object.values';
import url from 'url';

if (!Object.values) {
  values.shim();
}
const { URL: NodeURL } = url;

let adStateKey = 0;
let componentKey = 0;
let keyframeKey = 0;
let variantKey = 0;

const getUniqueAdStateKey = () => (adStateKey += 1).toString();

const getUniqueComponentKey = () => (componentKey += 1).toString();

const getUniqueKeyframeKey = () => (keyframeKey += 1).toString();

const getUniqueVariantKey = () => (variantKey += 1).toString();

const getByString = (o, s) => {
  s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  s = s.replace(/^\./, '');           // strip a leading dot
  const a = s.split('.');
  for (let i = 0, n = a.length; i < n; ++i) {
    const k = a[i];
    if (k in o) {
      o = o[k];
    } else {
      return;
    }
  }
  return o;
};
const assignByString = (obj, path, value) => {
  const props = path.split('.');

  let i = 0;
  for (; i < props.length - 1; i++) {
    obj = obj[props[i]];
  }
  obj[props[i]] = value;
};
const assignByArray = (obj, path, value) => {
  const props = path.split(',');

  let i = 0;
  for (; i < props.length - 1; i++) {
    obj = obj[props[i]];
  }
  obj[props[i]] = value;
};
export const getAssetIdFromString = (src) => {
  let path;
  if (typeof URL !== 'undefined') {
    path = new URL(src, typeof location === 'undefined' ? 'https://localhost' : location).pathname;
  } else {
    path = new NodeURL(src, typeof location === 'undefined' ? 'https://localhost' : location).pathname;
  }
  if (!(path).match(/(\/(\d+)-)/)) { return null; }
  return parseInt(((path).match(/(\/(\d+)-)/)[0]).match(/\d+/)[0], 10);
};

const arrangeAdStates = (adData) => {
  // default pack rectangles
  let nextRowY = 0;
  const margin = 25;
  const canvasSize = 30000;
  for (let i = 0; i < (adData.length / 2); i++) {
    adData[i].dimensions.left = margin;
    adData[i].dimensions.top = nextRowY;
    const otherIdx = (adData.length - (i + 1));
    if (otherIdx !== i) {
      adData[otherIdx].dimensions.left = adData[i].dimensions.left + adData[i].dimensions.width + margin * 4;
      adData[otherIdx].dimensions.top = nextRowY;
    }
    nextRowY += adData[i].dimensions.height + (margin * 4);
  }
  for (let i = 0; i < adData.length; i++) {
    adData[i].dimensions.top += canvasSize / 2;
    adData[i].dimensions.left += canvasSize / 2;
  }
  return adData;
};

const getDimensionsFromViewport = (adState, adStateName, viewport) => {
  if (adState.viewport) {
    return {
      width: adState.viewport[0],
      height: adState.viewport[1],
    };
  }

  return {
    width: viewport[adStateName][0],
    height: viewport[adStateName][1],
  };
};


export const decorateSectionsWithPosition = (sections) => {
  let lastHeight = 0;
  return Object.keys(sections).reduce((memo, key) => {
    const section = sections[key];

    const top = lastHeight;
    const height = parseInt(section.height, 10);
    const bottom = top + height;
    lastHeight = height;

    return {
      ...memo,
      [key]: { ...section, top, bottom },
    };
  }, {});
};

const extractSectionComponents = (section, height) => {
  const oldComponents = section.components || {};
  const componentKeys = Object.keys(oldComponents);

  return componentKeys.reduce((memo, key) => {
    const oldComponent = oldComponents[key];
    if (oldComponent.type !== 'script') {
      // eslint-disable-next-line no-use-before-define
      return [...memo, convertComponentToNewConfiguration(oldComponent, height)];
    }
    return memo;
  }, []);
};

export const extractScriptComponents = sections =>
  Object.values(sections)
    .map(section => Object.values(section.components)
        .filter(c => c.type === 'script')
        .map(c => c.properties['script-src']),
    )
    .join('');

const createVariantFromAdState = (oldAdState, name, width) => {
  const sections = decorateSectionsWithPosition(oldAdState.sections);

  const components = Object.values(sections).reduce((memo, section) =>
    [...memo, ...extractSectionComponents(section, section.top)]
  , []);

  return {
    name,
    components,
    script: extractScriptComponents(oldAdState.sections),
    key: getUniqueVariantKey(),
    dimensions: {
      width,
      height: Object.values(sections).reverse()[0].bottom,
    },
    backgroundColor: oldAdState.sections[0].background || '#ffffff',
  };
};

const extractVariantsFromAdState = (oldAdState, name, width) => {
  if (oldAdState.variants) { return oldAdState.variants; }
  return [createVariantFromAdState(oldAdState, name, width)];
};

const createArtboard = (oldAdState, name, viewport, index) => {
  const { width, height } = getDimensionsFromViewport(oldAdState, name, viewport);
  const [left = 0, top = 0] = oldAdState.viewportPosition || [0, 0];
  const animationSettings = oldAdState.animationSettings || { duration: 30, loop: 0 };
  const animationLoop = parseInt(animationSettings.loop, 10);
  const animationDuration = parseFloat(animationSettings.duration);

  const sections = decorateSectionsWithPosition(oldAdState.sections);

  const components = Object.values(sections).reduce((memo, section) =>
    [...memo, ...extractSectionComponents(section, section.top)]
  , []);

  return {
    name,
    components,
    key: getUniqueAdStateKey(),
    script: extractScriptComponents(oldAdState.sections),
    adState: parseInt(index, 10),
    selected: oldAdState.selected,
    dimensions: {
      left,
      top,
      width,
      height: Object.values(sections).reverse()[0].bottom,
    },
    viewport: {
      width,
      height,
    },
    offsetHeight: oldAdState.offsetHeight || 0,
    backgroundColor: sections[0].background || '#ffffff',
    borderColor: sections[0].borderColor || '#000000',
    borderWidth: sections[0].borderWidth || '0px',
    duration: animationDuration,
    loop: animationLoop,
    variants: extractVariantsFromAdState(oldAdState, name, width),
  };
};

export const createNewAdConfiguration = (oldAdConfiguration, viewport) => {
  let hadViewportPosition = false;
  const adStateKeys = Object.keys(oldAdConfiguration);

  let newConfiguration = adStateKeys.map((adStateName, index) => {
    const thisAdState = oldAdConfiguration[adStateName];

    if (!hadViewportPosition) {
      hadViewportPosition = !thisAdState.viewportPosition;
    }

    const newAdState = createArtboard(thisAdState, adStateName, viewport, index);
    return newAdState;
  });

  if (hadViewportPosition) {
    newConfiguration = arrangeAdStates(newConfiguration);
  }

  return newConfiguration;
};


const styleStringFromObject = (obj) => {
  const newObj = Object.assign({}, obj);

  if (newObj.translate) {
    newObj.transform = '';
    newObj.transform += `translate(${newObj.translate});`;
    delete newObj.translate;
  }

  const keys = Object.keys(newObj);
  let string = '';
  for (const key in keys) {
    string += `${keys[key]}:${newObj[keys[key]]};`;
  }
  return string;
};

const styleObjectFromString = (styleString) => {
  const style = {};
  const clearnStyleString = styleString.replace(/[\r\n]+(?=[^\r\n])/g, '').split(';'); // older ads

  for (const styleItem in clearnStyleString) {
    const stylePropertyPair = clearnStyleString[styleItem].split(':');
    if (stylePropertyPair[0] !== '') {
      style[stylePropertyPair[0]] = stylePropertyPair[1];
    }
  }
  return style;
};

export const convertEventsToActions = (thisComponent) => {
  const actions = {};

  if (!thisComponent.events) return actions;

  const eventType = thisComponent.events['event-type'] || '';
  const videoEvents = ['play-video', 'pause-video', 'replay-video', 'mute-video', 'unmute-video'];

  if (thisComponent.type === 'button') {
    actions.clickActions = eventType;

    if (eventType === 'click-through') {
      actions.clickthroughUrl = getByString(thisComponent, 'events.clickthrough-url');
    } else if (videoEvents.includes(eventType)) {
      const videoEvent = eventType.split('-')[0];
      const targetComponent = getByString(thisComponent, 'events.targetComponent');
      const imageComponent = getByString(thisComponent, 'events.imageComponent');
      actions[`${videoEvent}VideoTarget`] = targetComponent;
      actions[`${videoEvent}VideoImage`] = imageComponent;
    } else if (eventType === 'expand') {
      const targetArtboard = getByString(thisComponent, 'events.targetArtboard');
      actions.expandArtboardTarget = targetArtboard;
    } else if (eventType === 'switch-artboard') {
      const targetArtboard = getByString(thisComponent, 'events.targetArtboard');
      actions.switchArtboardTarget = targetArtboard;
    } else if (eventType === 'launch-overlay') {
      actions.clickActions = 'launch-lightbox';
      const targetArtboard = getByString(thisComponent, 'events.defaultArtboard');
      actions.lightboxDefaultArtboard = targetArtboard;
    } else if (eventType === 'show-component') {
      actions.clickActions = 'show';
      const targetComponent = getByString(thisComponent, 'events.targetComponent');
      actions.showComponent = targetComponent;
    } else if (eventType === 'hide-component') {
      actions.clickActions = 'hide';
      const targetComponent = getByString(thisComponent, 'events.targetComponent');
      actions.hideComponent = targetComponent;
    }
  }
  if (thisComponent.type === 'video') {
    if (eventType === 'switch-artboard') {
      const targetArtboard = getByString(thisComponent, 'events.targetArtboard');
      actions.switchArtboardTarget = targetArtboard;
    } else if (eventType === 'show-component') {
      actions.videoEndActions = 'show';
      const targetComponent = getByString(thisComponent, 'events.targetComponent');
      actions.showComponent = targetComponent;
    } else if (eventType === 'hide-component') {
      actions.videoEndActions = 'hide';
      const targetComponent = getByString(thisComponent, 'events.targetComponent');
      actions.hideComponent = targetComponent;
    }
  }

  return actions;
};

const generateComponentUid = key => key + (new Date()).getTime().toString(36);

const convertComponentToNewConfiguration = (thisComponent, height) => {
  const componentCss = cssUpperCase(cleanCss(thisComponent.css));
  if (typeof componentCss.top !== 'undefined') {
    componentCss.top += height;
  }

  const key = getUniqueComponentKey();
  const uid = thisComponent.uid === undefined ? generateComponentUid(key) : thisComponent.uid;
  const loop = thisComponent.loop || 1;
  const isHidden = componentCss.display === 'none' || !!thisComponent.hidden;
  const isLocked = !!thisComponent.locked;

  delete componentCss.display;
  delete componentCss.opacity; // this is to not overwrite animation opacity || this appears to alwayve be 1, added by the psd worker

  const newComponent = {
    key,
    uid,
    type: thisComponent.type,
    loop,
    settings: {
      locked: isLocked,
      hidden: isHidden,
      assetIds: [],
    },
    actions: {},
    keyframes: [],
  };

  const requiredSettings = requiredSettingsMapper[thisComponent.type];

  newComponent.actions = convertEventsToActions(thisComponent);

  for (const setting in requiredSettings) {
    const settingsKeyPair = requiredSettings[setting];
    const settingsKey = settingsKeyPair[0];
    const settingsMapper = settingsKeyPair[1];
    const settingValue = getByString(thisComponent, settingsMapper);

    if (settingsKey === 'assetIds') {
      newComponent.settings[settingsKey] = newComponent.settings[settingsKey] || [];

      const srcForProperties = getByString(thisComponent, settingsMapper);
      if (!srcForProperties || srcForProperties.indexOf('coreassets') !== -1) { // to make sure that we dont store default images
        continue;
      }
      const srcOrSrcs = srcForProperties.split(',').filter(src => src !== '');
      const assetIds = [];

      srcOrSrcs.forEach(src => assetIds.push(getAssetIdFromString(src)));
      newComponent.settings[settingsKey] = newComponent.settings[settingsKey].concat(assetIds);
    } else if (settingsKey === 'autoHide' || settingsKey === 'autoLoad' || settingsKey === 'autoPlay' || settingsKey === 'mask_contents') {
      newComponent.settings[settingsKey] = settingValue === 'checked';
    } else if (settingsKey === 'controls') {
      newComponent.settings[settingsKey] = settingValue === null || typeof settingValue === 'undefined' ? true : settingValue;
    } else if (['autoPlay', 'syncWithTimeline', 'mute', 'loop'].includes(settingsKey)) {
      newComponent.settings[settingsKey] = settingValue === null || typeof settingValue === 'undefined' ? false : settingValue;
    } else {
      newComponent.settings[settingsKey] = getByString(thisComponent, settingsMapper);
    }
  }

  if (typeof thisComponent.actions !== 'undefined') {
    Object.keys(thisComponent.actions).forEach((action) => {
      newComponent.actions[action] = thisComponent.actions[action];
    });
  }


  if (!componentCss.transformOrigin) {
    componentCss.transformOrigin = '50% 50%';
  }

  newComponent.keyframes[0] = componentCss;
  newComponent.keyframes[0].key = getUniqueKeyframeKey();
  newComponent.keyframes[0].t = -1;
  newComponent.keyframes[0].ease = 'none';
  newComponent.currentStyle = newComponent.keyframes[0];

  const requiredCss = requiredCssMapper[thisComponent.type];

  if (typeof requiredCss !== 'undefined') {
    const additionalCss = {};
    for (const css in requiredCss) {
      const cssKeyPair = requiredCss[css];
      if (cssKeyPair[0] === 'scale') {
        newComponent.keyframes[0].scaleX = getByString(thisComponent, cssKeyPair[1]);
        newComponent.keyframes[0].scaleY = getByString(thisComponent, cssKeyPair[1]);
      } else {
        newComponent.keyframes[0][cssKeyPair[0]] = getByString(thisComponent, cssKeyPair[1]);
      }
    }

    Object.assign(newComponent.keyframes[0], cleanCss(additionalCss));
  }

  if (typeof thisComponent.animation !== 'undefined' && typeof thisComponent.animation.type !== 'undefined') {
    const markerKeys = Object.keys(thisComponent.animation.markers);
    const delay = parseFloat(thisComponent.animation['timeline-delay']);
    let currentWidth;
    let currentHeight;

    for (const marker in markerKeys) {
      const markerId = markerKeys[marker];
      const thisMarker = thisComponent.animation.markers[markerId];
      let markerCss = styleObjectFromString(thisMarker.css); // could this be better to get rid of cssRemoveWhiteSpaceFromKeys

      markerCss = cleanCss(markerCss);
      markerCss = cssRemoveWhiteSpaceFromKeys(markerCss); // ["scale ", " transform-origin "] these can exist
      markerCss = cssUpperCase(markerCss);

      if (typeof markerCss.top !== 'undefined') {
        markerCss.top += height;
      }

      const transformAnimations = ['scale', 'scaleX', 'scaleY', 'rotateX', 'rotateY', 'rotateZ'];
      let markerCssKeys = Object.keys(markerCss);
      const findOne = function (haystack, arr) {
        return arr.some(v => haystack.indexOf(v) >= 0);
      };

      if (markerCssKeys.indexOf('transformOrigin') === -1 && findOne(markerCssKeys, transformAnimations)) {
        markerCss.transformOrigin = '50% 50%';
      }

      const newKeyframe = markerCss; // Object.assign({}, markerCss)
      newKeyframe.t = delay + parseFloat(thisMarker['time-position']);
      newKeyframe.ease = thisMarker.ease || 'none';
      newKeyframe.key = getUniqueKeyframeKey();
      newKeyframe.actions = {};
      if (typeof thisMarker.actions !== 'undefined') {
        Object.keys(thisMarker.actions).forEach((action) => {
          newKeyframe.actions[action] = thisMarker.actions[action];
        });
      }

      if (marker === '0') {
        // if there is an animation - set this marker's values as the default
        markerCssKeys = Object.keys(markerCss);
        for (let i = 0; i < markerCssKeys.length; i++) {
          if (markerCssKeys[i] !== 't' && markerCssKeys[i] !== 'key') {
            newComponent.keyframes[0][markerCssKeys[i]] = newKeyframe[markerCssKeys[i]];
          }
        }
      }

      // if this marker doesn't have dimensions - carry forward the current dimensions
      if (typeof newKeyframe.width === 'undefined') {
        newKeyframe.width = currentWidth || newComponent.keyframes[0].width;
      }
      if (typeof newKeyframe.height === 'undefined') {
        newKeyframe.height = currentHeight || newComponent.keyframes[0].height;
      }
      currentWidth = newKeyframe.width;
      currentHeight = newKeyframe.height;

      newComponent.keyframes.push(newKeyframe);
    }

    newComponent.keyframes.sort((keyframeA, keyframeB) => keyframeA.t - keyframeB.t);

    if (newComponent.keyframes.length > 1) { // this is to make sure that the first marker [default css] to the second marker doesn't animate [left, top, width, height]
      newComponent.keyframes[0].left = newComponent.keyframes[1].left || newComponent.keyframes[0].left;
      newComponent.keyframes[0].top = newComponent.keyframes[1].top || newComponent.keyframes[0].top;
      newComponent.keyframes[0].width = newComponent.keyframes[1].width || newComponent.keyframes[0].width;
      newComponent.keyframes[0].height = newComponent.keyframes[1].height || newComponent.keyframes[0].height;
    }
  }

  if (thisComponent.type === 'group') {
    const componentKeys = Object.keys(thisComponent.components);
    newComponent.subComponents = [];

    for (const groupComponent in componentKeys) {
      newComponent.subComponents.push(
        convertComponentToNewConfiguration(
          thisComponent.components[groupComponent],
          0,
        ),
      );
    }
  }

  return newComponent;
};


export const mapToOldAd = (adData, css, requiresFluidPlus, templateVersion, assets, uniqueAssetIds, fontUrls, maintainAspectRatio, formatId, revealFormat, activeFonts) => {
  const orientations = Object.values(adData).reduce((output, adState) => {
    output[adState.name] = [adState.viewport.width, adState.viewport.height];
    return output;
  }, {});

  const oldAdData = saveOldAdConfiguration(adData, assets);
  return {
    format_settings: JSON.stringify({
      activeFonts,
      maintainAspectRatio,
      revealFormat,
      css: {
        'css-src': css,
        'css-ext': fontUrls,
      },
      requiresFluidPlus,
      templateVersion,
    }),
    format_id: formatId,
    animation_settings: JSON.stringify({ delay: '0', duration: '30', loop: '0', timescale: '1' }),
    configuration: JSON.stringify(oldAdData),
    orientations: JSON.stringify(orientations),
    asset_ids: uniqueAssetIds,
  };
};

let permId = 1;

const saveOldAdConfiguration = (configuration, assets) => {
  const adConfiguration = {};
  permId = 1;

  configuration.map((adState) => {
    const components = {};

    adState.components.map((component, index) => {
      components[index] = convertComponentToOldConfiguration(component, index, assets);
    });

    if (adState.script !== '') {
      const index = Object.keys(components).length;
      components[index] = {
        id: index,
        permId: permId++,
        type: 'script',
        sectionid: 0,
        css: { left: 0, top: 0, height: 60, width: 60 },
        properties: { name: `script_${permId.toString()}`, 'script-src': adState.script },
      };
    }

    adConfiguration[adState.name] = {
      selected: adState.selected,
      viewport: [
        adState.viewport.width,
        adState.viewport.height,
      ],
      viewportPosition: [
        adState.dimensions.left,
        adState.dimensions.top,
      ],
      offsetHeight: adState.offsetHeight,
      animationSettings: {
        duration: adState.duration.toString(),
        timescale: '1',
        loop: adState.loop.toString(),
        delay: '0',
      },
      sections: {
        0: {
          background: adState.backgroundColor,
          borderColor: adState.borderColor,
          borderWidth: adState.borderWidth,
          height: adState.dimensions.height,
          components,
        },
      },
    };
  });

  return adConfiguration;
};

const convertComponentToOldConfiguration = (component, index, assets, group = undefined) => {
  const firstStyle = component.keyframes[0].style;
  const css = {
    left: `${firstStyle.left.toString()}px`,
    top: `${firstStyle.top.toString()}px`,
    width: `${firstStyle.width.toString()}px`,
    height: `${firstStyle.height.toString()}px`,
    opacity: firstStyle.opacity,
  };
  if (firstStyle.fontFamily) {
    css['font-family'] = firstStyle.fontFamily;
  }

  if (firstStyle.clip) {
    css.clip = firstStyle.clip;
  }

  if (firstStyle.blur) {
    css.blur = firstStyle.blur;
  }

  if (firstStyle.rotate || firstStyle.scale || firstStyle.translate) {
    css.transform = '';
    if (firstStyle.rotate) css.transform += `rotate(${firstStyle.rotate}deg);`;
    if (firstStyle.scale) css.transform += `scale(${firstStyle.scale});`;
    if (firstStyle.translate) css.transform += `rotate(${firstStyle.translate});`;
  }

  const thisComponent = {
    group,
    id: index,
    uid: component.uid,
    permId: permId++,
    type: component.type,
    sectionid: 0,
    hidden: component.settings.hidden,
    locked: component.settings.locked,
    css,
    loop: component.loop,
    properties: {},
  };

  if (component.type === 'button' || component.type === 'video' || component.type === 'wipeaway') {
    thisComponent.events = {};
    thisComponent.actions = {};
  }

  const requiredCss = requiredCssMapper[thisComponent.type];

  if (typeof requiredCss !== 'undefined') {
    for (const _css in requiredCss) {
      const cssKeyPair = requiredCss[_css];
      assignByString(thisComponent, cssKeyPair[1], firstStyle[cssKeyPair[0]]);
    }
  }

  const requiredSettings = requiredSettingsMapper[component.type];
  let hasAssetId = false;
  requiredSettings.map((settingsKeyPair) => {
    const settingsKey = settingsKeyPair[0];
    if (settingsKey === 'autoHide' || settingsKey === 'autoLoad' || settingsKey === 'autoPlay' || settingsKey === 'mask_contents') {
      return assignByString(thisComponent, settingsKeyPair[1], component.settings[settingsKey] ? 'checked' : '');
    }
    if (settingsKey === 'assetIds') {
      assignByString(thisComponent, settingsKeyPair[1], '');
      hasAssetId = true;
      return;
    }
    if (Array.isArray(component.settings[settingsKey])) {
      const stringValue = Array.isArray(component.settings[settingsKey]) ? component.settings[settingsKey].join() : component.settings[settingsKey];
      const value = stringValue || '';
      return assignByString(thisComponent, settingsKeyPair[1], value);
    }
    assignByString(thisComponent, settingsKeyPair[1], component.settings[settingsKey]);
  });

  if (component.actions) {
    Object.keys(component.actions).forEach((action) => {
      if (Array.isArray(component.actions[action])) {
        const stringValue = Array.isArray(component.actions[action]) ? component.actions[action].join() : component.actions[action];
        const value = stringValue || '';
        return assignByString(thisComponent, `actions.${action}`, value);
      }
      assignByString(thisComponent, `actions.${action}`, component.actions[action]);
    });
  }

  if (hasAssetId && component.settings.assetIds && Object.keys(component.settings.assetIds).length > 0) {
    const assetIds = Object.values(component.settings.assetIds);
    const componentAssets = assets.filter((asset) => { if (assetIds.includes(asset.id)) return asset; });
    const imageAsset = componentAssets.find(asset => asset.mime_type.includes('image'));
    const imageSrc = imageAsset ? imageAsset.asset_url : imageAsset;

    if (component.type === 'video') {
      const videoOrderByQuality = ['vbr.mp4', 'high.mp4', 'main.mp4', 'base.mp4', '.webm', '.ogg', '.3gp','.flv','.m3u8'];
      const videoAssetUrls = componentAssets.reverse().reduce((arr, asset) => asset.mime_type.includes('video') || asset.mime_type.includes('mpegurl') ? arr.concat([asset.asset_url]) : arr, []);
      const orderedVideoAssetUrls = videoOrderByQuality.map(videoQuality =>
        videoAssetUrls.find(videoAssetUrl => videoAssetUrl.includes(videoQuality)));
      assignByString(thisComponent, 'properties.poster-src', imageSrc);
      assignByString(thisComponent, 'properties.video-src', orderedVideoAssetUrls.join());
    } else if (component.type === '360' || component.type === 'swipegallery') {
      const imageAssetUrls = assets.filter(a => assetIds.includes(a.id)).map(a => a.asset_url);
      assignByString(thisComponent, 'properties.poster-src', imageAssetUrls[0]);
      assignByString(thisComponent, 'properties.image-src', imageAssetUrls.join());
    } else if (component.type === 'font') {
      const ttfAsset = componentAssets.find(asset => asset.mime_type.includes('application/x-font-ttf'));
      const woffAsset = componentAssets.find(asset => asset.mime_type.includes('application/x-font-woff'));
      const eotAsset = componentAssets.find(asset => asset.mime_type.includes('application/vnd.ms-fontobject'));
      const svgAsset = componentAssets.find(asset => asset.mime_type.includes('image/svg+xml'));

      assignByString(thisComponent, 'properties.ttf-src', ttfAsset ? ttfAsset.asset_url : '');
      assignByString(thisComponent, 'properties.woff-src', woffAsset ? woffAsset.asset_url : '');
      assignByString(thisComponent, 'properties.svg-src', eotAsset ? eotAsset.asset_url : '');
      assignByString(thisComponent, 'properties.eot-src', svgAsset ? svgAsset.asset_url : '');
    } else if (component.type === 'imagezoom') {
      assignByString(thisComponent, 'properties.poster-src', imageSrc);
      assignByString(thisComponent, 'properties.zoom-src', imageSrc);
    } else if (component.type === 'image') {
      assignByString(thisComponent, 'properties.src', imageSrc);
    } else if (component.type === 'wipeaway') {
      assignByString(thisComponent, 'properties.src', imageSrc);
    }
  }

  if (component.keyframes.length > 1) {
    const originKeyframes = component.keyframes;
    const animatedKeyframes = Object.assign([], originKeyframes);
    animatedKeyframes.splice(0, 1); // first marker is used to set css

    const animationStartTime = animatedKeyframes[0].t;
    thisComponent.animation = {};
    thisComponent.animation.type = 'timeline';
    thisComponent.animation['timeline-delay'] = animationStartTime.toFixed(2);
    thisComponent.animation['timeline-id'] = '-1';

    thisComponent.animation.markers = {};

    animatedKeyframes.map((keyframe, index) => {
      const style = Object.assign({}, keyframe.style);
      const previousStyle = originKeyframes[index].style;
      delete style.ease;

      if (parseInt(previousStyle.width, 10) === parseInt(style.width, 10)) {
        delete style.width;
      }

      if (parseInt(previousStyle.height, 10) === parseInt(style.height, 10)) {
        delete style.height;
      }

      thisComponent.animation.markers[index] = {
        'time-position': (keyframe.t - animationStartTime).toFixed(2),
        ease: keyframe.style.ease,
        css: styleStringFromObject(style),
        actions: keyframe.actions,
      };
    });
  }

  if (component.type === 'textbox') {
    const saveStyle = Object.assign({}, component.keyframes[0].style);
    const outputStyle = {};
    const styleKeys = Object.keys(saveStyle);
    for (let i = 0; i < styleKeys.length; i++) {
      outputStyle[styleKeys[i].replace(/([A-Z])/g, g => `-${g[0].toLowerCase()}`)] = saveStyle[styleKeys[i]];
    }
    thisComponent.css = outputStyle;
  }

  if (component.type === 'group') {
    thisComponent.components = {};
    component.subComponents.map((groupComponent, index) => {
      thisComponent.components[index] = convertComponentToOldConfiguration(groupComponent, index, assets, thisComponent.id);
    });
  }
  return thisComponent;
};

const cssRemoveWhiteSpaceFromKeys = (cssObject) => {
  const markerCssKeys = Object.keys(cssObject);
  const cssObjectWithOutWhiteSpace = {};

  for (let i = 0; i < markerCssKeys.length; i++) {
    const thisMarkerCssKey = markerCssKeys[i];
    cssObjectWithOutWhiteSpace[thisMarkerCssKey.replace(/\s/g, '')] = cssObject[thisMarkerCssKey];
  }

  return cssObjectWithOutWhiteSpace;
};
const cssUpperCase = (cssObject) => {
  const toCamel = function (string) {
    return string.replace(/(\-[a-z])/g, $1 => $1.toUpperCase().replace('-', ''));
  };

  return changeCase(cssObject, toCamel);
};
const changeCase = (cssObject, changeCaseFunction) => {
  const markerCssKeys = Object.keys(cssObject);

  for (let i = 0; i < markerCssKeys.length; i++) {
    const thisMarkerCssKey = markerCssKeys[i];
    if (thisMarkerCssKey.indexOf('-') !== -1) {
      const newMarkerCssKey = changeCaseFunction(thisMarkerCssKey);
      cssObject[newMarkerCssKey] = cssObject[thisMarkerCssKey];
      delete cssObject[thisMarkerCssKey];
    }
  }

  return cssObject;
};
const parseCssShorthand = (valueToFind, cssDeclaration) => {
  const value = `${valueToFind}(`;
  let startPos = cssDeclaration.indexOf(value);
  if (startPos <= -1) { return ''; }
  startPos += value.length;
  const endPos = cssDeclaration.indexOf(')', startPos);
  return cssDeclaration.substring(startPos, endPos);
};
const cleanCss = (cssObject) => {
  const cssToClean = ['left', 'top', 'width', 'height', 'borderRadius', 'borderWidth'];
  const cssToCleanFloat = ['opacity', 'scale', 'scaleX', 'scaleY'];
  for (const key in cssToClean) {
    const cssKey = cssToClean[key];
    if (typeof cssObject[cssKey] !== 'undefined') {
      cssObject[cssKey] = parseInt(cssObject[cssKey]);
    }
  }
  for (const keyFloat in cssToCleanFloat) {
    const cssKeyFloat = cssToCleanFloat[keyFloat];
    if (typeof cssObject[cssKeyFloat] !== 'undefined') {
      cssObject[cssKeyFloat] = parseFloat(cssObject[cssKeyFloat]);
    }
  }
  if (typeof cssObject.transform !== 'undefined') {
    const scale = parseCssShorthand('scale', cssObject.transform);
    if (scale !== '') cssObject.scale = parseFloat(scale);
    const rotate = parseCssShorthand('rotate', cssObject.transform);
    if (rotate !== '') cssObject.rotate = parseFloat(rotate);
    const translate = parseCssShorthand('translate', cssObject.transform);
    if (translate !== '') cssObject.translate = translate;
    delete cssObject.transform;
  }
  return cssObject;
};

const requiredCssMapper = {
  rectangle: [
    ['backgroundColor', 'properties.color'],
    ['borderWidth', 'properties.border-size'],
    ['borderColor', 'properties.border-color'],
    ['borderRadius', 'properties.border-radius'],
    ['borderStyle', 'properties.border-style'],
  ],
};

const requiredSettingsMapper = {
  image: [
    ['name', 'properties.name'],
    ['assetIds', 'properties.src'],
    ['fitting', 'properties.fitting'],
    ['position', 'properties.position'],
    ['positionCustom', 'properties.positionCustom'],
    ['autoHide', 'properties.auto-hide'],
    ['pinned', 'properties.pinned'],
  ],
  button: [
    ['name', 'properties.name'],
    ['autoHide', 'properties.auto-hide'],
    ['pinned', 'properties.pinned'],
  ],
  rectangle: [
    ['name', 'properties.name'],
    ['autoHide', 'properties.auto-hide'],
    ['pinned', 'properties.pinned'],
  ],
  360: [ // assetIds []
    ['name', 'properties.name'],
    /* ["assetIds"         , "properties.poster-src"], poster file is just the first image in src */
    ['assetIds', 'properties.image-src'],
    ['autoHide', 'properties.auto-hide'],
  ],
  wipeaway: [
    ['name', 'properties.name'],
    ['assetIds', 'properties.src'],
    ['brushComponent', 'properties.brushComponent'],
    ['autoHide', 'properties.auto-hide'],
    ['pinned', 'properties.pinned'],
  ],
  effectlayer: [ // src []
    ['name', 'properties.name'],
    ['effect', 'properties.effect'],
    ['size', 'properties.size'],
    ['speed', 'properties.speed'],
    ['intensity', 'properties.intensity'],
    ['autoHide', 'properties.auto-hide'],
    ['pinned', 'properties.pinned'],
  ],
  swipegallery: [ // src []
    ['name', 'properties.name'],
    /* ["assetIds"      , "properties.poster-src"], poster file is just the first image in src*/
    ['assetIds', 'properties.image-src'],
    ['autoPlayDelay', 'properties.auto-play-delays'],
    ['autoPlay', 'properties.auto-plays'],
    ['autoHide', 'properties.auto-hide'],
    ['pinned', 'properties.pinned'],
  ],
  imagezoom: [
    ['name', 'properties.name'],
    ['assetIds', 'properties.poster-src'],
    ['assetIds', 'properties.zoom-src'],
    ['zoomType', 'properties.zoom-type'],
    ['autoHide', 'properties.auto-hide'],
    ['pinned', 'properties.pinned'],
  ],
  textbox: [
    ['name', 'properties.name'],
    ['text', 'text'],
    ['WFPFontId', 'properties.WFPFontId'],
    ['WFPWeightId', 'properties.WFPWeightId'],
    ['autoHide', 'properties.auto-hide'],
    ['pinned', 'properties.pinned'],
  ],
  video: [ // src []
    ['name', 'properties.name'],
    ['assetIds', 'properties.poster-src'],
    ['assetIds', 'properties.video-src'],
    ['fitting', 'properties.fitting'],
    ['position', 'properties.position'],
    ['positionCustom', 'properties.positionCustom'],
    ['controls', 'properties.controls'],
    ['autoPlay', 'properties.auto-play'],
    ['mute', 'properties.mute'],
    ['loop', 'properties.loop'],
    ['autoHide', 'properties.auto-hide'],
    ['syncWithTimeline', 'properties.syncWithTimeline'],
    ['pinned', 'properties.pinned'],
  ],
  youtube: [
    ['name', 'properties.name'],
    ['youtubeURL', 'properties.youtube-url'],
    ['autoHide', 'properties.auto-hide'],
    ['autoPlay', 'properties.auto-play'],
    ['controls', 'properties.controls'],
    ['pinned', 'properties.pinned'],
  ],
  font: [ // src []
    ['name', 'properties.name'],
    ['assetIds', 'properties.ttf-src'],
    ['assetIds', 'properties.woff-src'],
    ['assetIds', 'properties.svg-src'],
    ['assetIds', 'properties.eot-src'],
  ],
  script: [
    ['name', 'properties.name'],
    ['script', 'properties.script-src'],
  ],
  group: [
    ['name', 'properties.name'],
    ['hover', 'properties.hover'],
    ['hoverTransitionEffect', 'properties.hoverTransitionEffect'],
    ['behaviour', 'properties.behaviour'],
    ['autoPlay', 'properties.autoPlay'],
    ['autoPlayDirection', 'properties.autoPlayDirection'],
    ['autoPlayAmount', 'properties.autoPlayAmount'],
    ['autoPlayDelay', 'properties.autoPlayDelay'],
    ['carouselLoop', 'properties.carouselLoop'],
    ['carouselLoopTransition', 'properties.carouselLoopTransition'],
    ['pannableAxis', 'properties.pannableAxis'],
    ['mask_contents', 'properties.mask-contents'],
    ['autoHide', 'properties.auto-hide'],
    ['pinned', 'properties.pinned'],
  ],
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Builder/legacy/adMapper.js